// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Check, Search } from "lucide-react";
import { useDeferredValue, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TaskIcon, useMaterialIconSet } from "@/features/tasks/task-icon";
import { cn } from "@/lib/utils";

/** How many matches the grid draws. */
const MAX_RESULTS = 300;

interface MaterialIconPickerProps {
  value: string | null | undefined;
  onChange: (icon: string) => void;
  disabled?: boolean;
}

/**
 * Search the bundled Material icons by name.
 *
 * The set is limited to names the Action Tool can resolve, so anything chosen here renders on the
 * handheld.
 */
export function MaterialIconPicker({ value, onChange, disabled }: MaterialIconPickerProps) {
  const { t } = useTranslation("back-office");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const iconSet = useMaterialIconSet();

  const names = useMemo(() => (iconSet ? Object.keys(iconSet) : []), [iconSet]);

  const matches = useMemo(() => {
    const needle = deferredQuery.trim().toLowerCase();
    if (!needle) return names.slice(0, MAX_RESULTS);
    const found: string[] = [];
    for (const name of names) {
      if (name.toLowerCase().includes(needle)) {
        found.push(name);
        if (found.length >= MAX_RESULTS) break;
      }
    }
    return found;
  }, [names, deferredQuery]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled} className="w-full justify-start gap-2 font-normal">
          <TaskIcon icon={value} className="h-4 w-4" />
          <span className="truncate font-mono text-xs">{value || t("tasks.editor.icon.none")}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[22rem] p-0">
        <div className="relative border-b p-2">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("tasks.editor.icon.searchPlaceholder")}
            className="pl-8"
          />
        </div>
        {iconSet ? (
          <div className="max-h-72 overflow-y-auto p-2">
            <div className="grid grid-cols-8 gap-1">
              {matches.map((name) => (
                <button
                  key={name}
                  type="button"
                  title={name}
                  onClick={() => {
                    onChange(name);
                    setOpen(false);
                  }}
                  className={cn(
                    "relative flex h-9 items-center justify-center rounded hover:bg-accent",
                    name === value && "bg-accent ring-1 ring-primary",
                  )}
                >
                  <TaskIcon icon={name} className="h-5 w-5" />
                  {name === value && <Check className="absolute right-0 top-0 h-3 w-3 text-primary" />}
                </button>
              ))}
            </div>
            {!matches.length && (
              <p className="p-4 text-center text-sm text-muted-foreground">{t("tasks.editor.icon.noMatches")}</p>
            )}
            {matches.length >= MAX_RESULTS && (
              <p className="p-2 text-center text-xs text-muted-foreground">
                {t("tasks.editor.icon.tooManyMatches", { count: MAX_RESULTS })}
              </p>
            )}
          </div>
        ) : (
          <InlineSpinner label={t("tasks.editor.icon.loading")} className="py-8" />
        )}
      </PopoverContent>
    </Popover>
  );
}
